import { useEffect, useState } from "react";
import { Download, FileCheck2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { EmptyState, PageHeader, Pager } from "@/components/shared/Common";
import { useAuth } from "@/context/AuthContext";
import { api, download, errMsg } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const Json = ({ label, value }) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wider text-[#5b6656] mb-1">{label}</div>
    <pre className="rounded-lg bg-[#0f1a0b] text-[#d6f5c4] p-3 text-xs overflow-x-auto max-h-64">{value ? JSON.stringify(value, null, 2) : "—"}</pre>
  </div>
);

export const AuditTable = ({ fixed = {}, filters = {} }) => {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, page_size: 25 });
  const [detail, setDetail] = useState(null);
  const key = JSON.stringify({ ...fixed, ...filters });
  useEffect(() => { setPage(1); }, [key]);
  useEffect(() => { api.get("/admin/audit", { params: { ...fixed, ...filters, page } }).then((r) => setData(r.data)); }, [key, page]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="syc-card overflow-hidden" data-testid="audit-table">
      {data.items.length === 0 ? <EmptyState title="Sin eventos registrados" /> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-[#5b6656] bg-[#fafbf9]">
              <th className="px-4 py-3">#</th><th className="px-4 py-3">Fecha/hora</th><th className="px-4 py-3">Usuario</th><th className="px-4 py-3">Módulo</th><th className="px-4 py-3">Acción</th><th className="px-4 py-3">IP</th>
            </tr></thead>
            <tbody>
              {data.items.map((a) => (
                <tr key={a.id} data-testid={`audit-row-${a.seq}`} onClick={() => setDetail(a)} className="border-b border-[#f0f2ee] cursor-pointer hover:bg-[#f6fbf2]">
                  <td className="px-4 py-2.5 font-mono text-xs text-[#8a9584]">{a.seq}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap text-xs">{fmtDateTime(a.ts)}</td>
                  <td className="px-4 py-2.5">{a.user_name || "—"}</td>
                  <td className="px-4 py-2.5 text-xs">{a.module}</td>
                  <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#1c5f00]">{a.action}</td>
                  <td className="px-4 py-2.5 font-mono text-xs text-[#5b6656]">{a.ip || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <Pager page={page} total={data.total} pageSize={data.page_size} onPage={setPage} />
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="audit-detail-dialog">
          <DialogHeader><DialogTitle className="font-mono text-base">{detail?.action}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><b>Fecha:</b> {fmtDateTime(detail.ts)}</div><div><b>Usuario:</b> {detail.user_name || "—"}</div>
                <div><b>IP:</b> {detail.ip || "—"}</div><div><b>Recurso:</b> {detail.resource_type || "—"} {detail.resource_id || ""}</div>
              </div>
              <Json label="Valor anterior" value={detail.before} />
              <Json label="Valor nuevo" value={detail.after} />
              <div className="font-mono text-[10px] text-[#8a9584] break-all">hash {detail.hash}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default function Audit() {
  const { can } = useAuth();
  const [f, setF] = useState({ module: "all", action: "", q: "", date_from: "", date_to: "" });
  const [modules, setModules] = useState([]);
  useEffect(() => { api.get("/admin/audit", { params: { page_size: 1 } }).then((r) => setModules(r.data.modules)); }, []);
  const params = { ...f, module: f.module === "all" ? "" : f.module };

  const verify = async () => {
    try {
      const { data } = await api.get("/admin/audit/verify");
      data.ok ? toast.success(`Cadena íntegra: ${data.checked} registros verificados`) : toast.error(`Cadena alterada en el registro #${data.broken_at_seq}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const exportXlsx = () => download("/admin/audit/export", params, "auditoria_syc.xlsx").catch((e) => toast.error(errMsg(e)));
  const certificate = () => download("/admin/audit/certificate", {}, "certificado_auditoria.pdf").then(() => toast.success("Certificado generado")).catch((e) => toast.error(errMsg(e)));

  return (
    <div data-testid="audit-page">
      <PageHeader eyebrow="Administración" title="Auditoría" subtitle="Registro inmutable con encadenamiento hash: quién, qué, cuándo, dónde y qué cambió."
        actions={<>
          <Button data-testid="verify-chain-btn" variant="outline" className="rounded-full" onClick={verify}><ShieldCheck size={15} className="mr-1" />Verificar integridad</Button>
          <Button data-testid="audit-certificate-btn" variant="outline" className="rounded-full" onClick={certificate}><FileCheck2 size={15} className="mr-1" />Descargar certificado PDF</Button>
          {can("admin.auditoria.exportar") && <Button data-testid="export-audit-btn" className="rounded-full" onClick={exportXlsx}><Download size={15} className="mr-1" />Exportar Excel</Button>}
        </>} />
      <div className="syc-card p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <Select value={f.module} onValueChange={(v) => setF({ ...f, module: v })}>
          <SelectTrigger data-testid="audit-module-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Todos los módulos</SelectItem>{modules.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="audit-action-filter" placeholder="Acción (ej. LOGIN)" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} />
        <Input data-testid="audit-user-filter" placeholder="Usuario" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} />
        <Input data-testid="audit-date-from" type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <Input data-testid="audit-date-to" type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
      </div>
      <AuditTable filters={params} />
    </div>
  );
}
